import {
  useCreateInvite,
  type InviteResponse,
} from '@/organization/useOrganizations';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { InviteSuccessDialog } from './InviteSuccessDialog';

const roles = [
  'Organization Admin',
  'Program Manager',
  'Budget Manager',
  'General User',
];

interface Props {
  open: boolean;
  onClose: () => void;
  organizationId: string;
}

export function InviteUserModal({ open, onClose, organizationId }: Props) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('General User');
  const [sendEmail, setSendEmail] = useState(false);
  const [inviteResponse, setInviteResponse] = useState<InviteResponse | null>(
    null,
  );
  const [showSuccess, setShowSuccess] = useState(false);

  const inviteMutation = useCreateInvite(organizationId);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    inviteMutation.mutate(
      { email, sendEmail, role },
      {
        onSuccess: (data) => {
          setInviteResponse(data);
          setShowSuccess(true);
          // Reset form
          setEmail('');
          setSendEmail(false);
        },
      },
    );
  };

  const handleCloseSuccess = () => {
    setShowSuccess(false);
    setInviteResponse(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>Invite New User</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Stack>
            <TextField
              label='Email'
              type='email'
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              fullWidth
              autoFocus
            />
            <FormControl>
              <InputLabel>Role</InputLabel>
              <Select
                value={role}
                onChange={(e) => setRole(e.target.value)}
                fullWidth
                label='Role'
              >
                {roles.map((r) => (
                  <MenuItem key={r} value={r}>
                    {r}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Typography variant='caption' color='text.secondary' sx={{ pl: 2 }}>
              User will be able to: <br />
              - Add, edit, copy, or archive projects <br />
              {role === 'Organization Admin' && (
                <>
                  - Add, edit, or delete fiscal years, departments, and users
                  <br />
                </>
              )}
              - Asssign projects to fiscal years <br />
              {role === 'Organization Admin' && (
                <>
                  - Edit organization settings <br />
                </>
              )}
              {(role === 'Program Manager' ||
                role === 'Organization Admin') && (
                <>
                  - Approve project designs <br />
                </>
              )}
              {(role === 'Budget Manager' || role === 'Organization Admin') && (
                <>
                  - Approve project and fiscal year budget <br />
                </>
              )}
            </Typography>
            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={sendEmail}
                    onChange={(e) => setSendEmail(e.target.checked)}
                    color='primary'
                  />
                }
                label={
                  <Box>
                    <Typography variant='body2'>
                      Send invitation email
                    </Typography>
                    <Typography variant='caption' color='text.secondary'>
                      An email with signup instructions will be sent to the
                      recipient
                    </Typography>
                  </Box>
                }
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type='submit'
            variant='contained'
            disabled={inviteMutation.isPending}
          >
            {sendEmail ? 'Send Invite' : 'Create Invite Link'}
          </Button>
        </DialogActions>
      </form>

      {/* Success dialog */}
      <InviteSuccessDialog
        open={showSuccess}
        onClose={handleCloseSuccess}
        inviteData={inviteResponse}
      />
    </Dialog>
  );
}
